// Note: This is not a copy of dbConstants on the server. Since the enums are updated to
// graphql supported format, we don't need the label mapping on the server anymore

import {
  CertificateStatusEnum,
  EventTypeEnum,
  GocStatusEnum,
  InsulationHeatTracingEnum,
  InsulationMaterialsEnum,
  InsulationStatusEnum,
  IsolationDrawingStatusEnum,
  IsolationLineWalkPackStatusEnum,
  IsolationSourceEnum,
  JobFieldWalkStatusEnum,
  JobScheduleStatusEnum,
  LeakTestPackReviewEnum,
  LeakTestPackStatusEnum,
  LeakTestPackTypeEnum,
  LiftplanEquipmentStatusEnum,
  LiftplanStatusEnum,
  LiftplanStructuralAssessmentEnum,
  LiftplanTypeEnum,
  MediaMetadataMimeEnum,
  PermitRequirementEnum,
  PermitTimingEnum,
  PermitWalkedEnum,
  ProcedureCategoryEnum,
  ProcedureStatusEnum,
  PurchaseOrderStatusEnum,
  PurchaseOrderTypeEnum,
  ScaffoldStatusEnum,
  WorkpackStatusEnum,
  WorkpackTypeEnum,
} from '../../graph-queries/__generated__/graphql';
import { stringToOption } from '../util/format';

export const DB_MAX_STRING_LENGTH = 2000;

export const WORKPACK_TYPE_ENUM: Record<WorkpackTypeEnum, string> = {
  VENDOR: 'Vendor',
  NON_VENDOR: 'Non Vendor',
};

export const WORKPACK_STATUS_ENUM: Record<WorkpackStatusEnum, string> = {
  REQUIRED: 'Required',
  DRAFT: 'Draft',
  ISSUED_FOR_REVIEW: 'Issued for Review',
  APPROVED_FOR_USE: 'Approved for Use',
};

export const SCAFFOLD_STATUS_ENUM: Record<ScaffoldStatusEnum, string> = {
  SURVEY_REQUIRED: 'Survey Required',
  SURVEY_COMPLETE: 'Survey Complete',
  ERECTED: 'Erected',
};

export const LIFTPLAN_TYPE_ENUM: Record<LiftplanTypeEnum, string> = {
  VENDOR: 'Vendor',
  NON_VENDOR: 'Non Vendor',
};

export const LIFTPLAN_EQUIPMENT_STATUS_ENUM: Record<LiftplanEquipmentStatusEnum, string> = {
  REQUIRED: 'Required',
  IDENTIFIED: 'Identified',
  ORDERED: 'Ordered',
};

export const LIFTPLAN_STRUCTURAL_ASSESSMENT_ENUM: Record<LiftplanStructuralAssessmentEnum, string> =
  {
    REQUIRED: 'Required',
    NOT_REQUIRED: 'Not Required',
  };

export const LIFTPLAN_STATUS_ENUM: Record<LiftplanStatusEnum, string> = {
  REQUIRED: 'Required',
  DRAFT: 'Draft',
  ISSUED_FOR_REVIEW: 'Issued for Review',
  APPROVED_FOR_USE: 'Approved for Use',
};

export const INSULATION_STATUS_ENUM: Record<InsulationStatusEnum, string> = {
  REQUIRED: 'Required',
  SURVEYED: 'Surveyed',
  REMOVED: 'Removed',
  REPLACED: 'Replaced',
};
export const INSULATION_MATERIALS_ENUM: Record<InsulationMaterialsEnum, string> = {
  FROM_VENDOR: 'From Vendor',
  REQUIRED: 'Required',
  NOT_REQUIRED: 'Not Required',
  PO_ADDED: 'PO Added',
};

export const INSULATION_HEAT_TRACING_ENUM: Record<InsulationHeatTracingEnum, string> = {
  NA: 'NA',
  REQUIRED: 'Required',
  PLANNED_FIELD_DISCONNECT: 'Planned - Field Disconnect',
  PLANNED_CB_ISOLATION: 'Planned - CB Isolation',
};

export const ISOLATION_DRAWING_STATUS_ENUM: Record<IsolationDrawingStatusEnum, string> = {
  REQUIRED: 'Required',
  DRAFT: 'Draft',
  LIMITS_AGREED: 'Limits Agreed',
};

export const ISOLATION_SOURCE_ENUM: Record<IsolationSourceEnum, string> = {
  POWER_SUPPLY: 'Power Supply',
  DISCONNECT: 'Disconnect',
  NA: 'N/A',
};

export const ISOLATION_LINE_WALK_PACK_STATUS_ENUM: Record<IsolationLineWalkPackStatusEnum, string> =
  {
    REQUIRED: 'Required',
    NOT_REQUIRED: 'Not Required',
    DRAFT: 'Draft',
    ISSUED_FOR_REVIEW: 'Issued for Review',
    APPROVED_FOR_USE: 'Approved for Use',
  };

export const MEDIA_ALLOWED_MIME_TYPES: Record<MediaMetadataMimeEnum, string> = {
  JPEG: 'image/jpeg',
  PNG: 'image/png',
  PDF: 'application/pdf',
  MP4: 'video/mp4',
  DOC: 'application/msword', // .doc
  DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
};

export const PERMIT_TIMING_ENUM: Record<PermitTimingEnum, string> = {
  PRE_TAR: 'Pre-TAR',
  TAR: 'TAR',
  POST_TAR: 'Post-TAR',
};

export const LEAK_TEST_PACK_TYPE_ENUM: Record<LeakTestPackTypeEnum, string> = {
  VENDOR: 'Vendor',
  NON_VENDOR: 'Non Vendor',
};

export const LEAK_TEST_PACK_REVIEW_ENUM: Record<LeakTestPackReviewEnum, string> = {
  NOT_REQUIRED: 'Not Required',
  REQUIRED: 'Required',
  COMPLETE: 'Complete',
};

export const LEAK_TEST_PACK_STATUS_ENUM: Record<LeakTestPackStatusEnum, string> = {
  REQUIRED: 'Required',
  DRAFT: 'Draft',
  ISSUED_FOR_REVIEW: 'Issued for Review',
  APPROVED_FOR_USE: 'Approved for Use',
};

export const GOC_STATUS_ENUM: Record<GocStatusEnum, string> = {
  REQUIRED: 'Required',
  NOT_REQUIRED: 'Not Required',
  DRAFT: 'Draft',
  READY: 'Ready',
};
export const PROCEDURE_STATUS_ENUM: Record<ProcedureStatusEnum, string> = {
  REQUIRED: 'Required',
  DRAFT: 'Draft',
  ISSUED_FOR_REVIEW: 'Issued for Review',
  APPROVED_FOR_USE: 'Approved for Use',
};
export const PROCEDURE_CATEGORY_ENUM: Record<ProcedureCategoryEnum, string> = {
  RAMP_DOWN: 'Ramp Down',
  PLANT_PREP: 'Plant Prep',
  LEAK_TEST_OR_COMMISSIONING: 'Leak Test or Commissioning',
  START_UP: 'Start Up',
};

export const PERMIT_REQUIREMENT_ENUM: Record<PermitRequirementEnum, string> = {
  REQUIRED: 'Required',
  NOT_REQUIRED: 'Not Required',
};

export const PERMIT_WALKED_ENUM: Record<PermitWalkedEnum, string> = {
  REQUIRED: 'Required',
  NOT_REQUIRED: 'Not Required',
  PA_WALKED: 'PA Walked',
  AA_WALKED: 'AA Walked',
};

export const JOB_SCHEDULE_STATUS_ENUM: Record<JobScheduleStatusEnum, string> = {
  L1: 'L1',
  L2: 'L2',
  L3: 'L3',
  L4: 'L4',
};

export const JOB_FIELD_WALK_STATUS_ENUM: Record<JobFieldWalkStatusEnum, string> = {
  NOT_REQUIRED: 'Not Required',
  REQUIRED: 'Required',
  VIRTUAL_COMPLETE: 'Virtual Complete',
  FIELD_COMPLETE: 'Field Complete',
};

export const PURCHASE_ORDER_STATUS_ENUM: Record<PurchaseOrderStatusEnum, string> = {
  REQUIRED: 'Required',
  QUALITY_REVIEW_COMPLETE: 'Quality Review Complete',
  ISSUED: 'Issued',
};

export const PURCHASE_ORDER_TYPE_ENUM: Record<PurchaseOrderTypeEnum, string> = {
  MATERIAL: 'Material',
  SPECIALIST_TOOLING: 'Specialist Tooling',
  SPECIALIST_VENDOR: 'Specialist Vendor',
};

// All options which are not common are put into other
export const ECOW_PERMIT_STATUS_MAPPING = {
  Required: { label: 'Required', isCommon: true, priority: 0 },
  Requesting: { label: 'Draft', isCommon: true, priority: 1 },
  Approved: { label: 'TRA Approved', isCommon: true, priority: 2 },
  ReadyToVerify: { label: 'Ready To Verify', isCommon: true, priority: 3 },
  Verified: { label: 'Verified', isCommon: true, priority: 4 },

  Active: { label: 'Active', isCommon: false, priority: 5 },
  Authorised: { label: 'Authorised', isCommon: false, priority: 5 },
  Closed: { label: 'Closed', isCommon: false, priority: 5 },
  Closing: { label: 'Closing', isCommon: false, priority: 5 },
  Expired: { label: 'Expired', isCommon: false, priority: 5 },
  Issued: { label: 'Issued', isCommon: false, priority: 5 },
  ReAuthorized: { label: 'Re Authorized', isCommon: false, priority: 5 },
  ReIssued: { label: 'Re Issued', isCommon: false, priority: 5 },
  Revalidate: { label: 'Revalidate', isCommon: false, priority: 5 },
  SuspendedForEmergency: { label: 'Suspended For Emergency', isCommon: false, priority: 5 },
  SuspendedForReauthorization: {
    label: 'Suspended For Reauthorization',
    isCommon: false,
    priority: 5,
  },
  SuspendedForRevalidation: { label: 'Suspended For Revalidation', isCommon: false, priority: 5 },
  SuspendedForSanctionToTest: {
    label: 'Suspended For Sanction To Test',
    isCommon: false,
    priority: 5,
  },
};

export const ECOW_INUSLATION_STATUS_MAPPING = {
  required: { label: 'Required', isCommon: true, priority: 0 },
  surveyed: { label: 'Surveyed', isCommon: true, priority: 1 },
  removed: { label: 'Removed', isCommon: true, priority: 2 },
  replaced: { label: 'Replaced', isCommon: true, priority: 3 },
};

export const EMOC_STATUS_MAPPING = {
  Required: { label: 'Required', isCommon: true, priority: 0 },
  DRAFT: { label: 'Draft', isCommon: true, priority: 1 },
  VERIFIED: { label: 'Verified', isCommon: true, priority: 2 },
  'COORDINATOR ASSIGNED': { label: 'Coordinator Assigned', isCommon: true, priority: 3 },
  REVIEWED: { label: 'Reviewed', isCommon: true, priority: 3 },
  APPROVED: { label: 'Approved', isCommon: true, priority: 4 },
  'WAITING AUTHORIZATION': { label: 'Waiting Authorization', isCommon: true, priority: 5 },
  AUTHORIZED: { label: 'Authorized', isCommon: true, priority: 5 },
  HOLD: { label: 'Hold', isCommon: true, priority: 5 },
  'PENDING CLOSURE': { label: 'Pending Closure', isCommon: true, priority: 5 },
  'AUTHORIZED - EXT1': { label: 'Authorized - Ext1', isCommon: true, priority: 5 },
  'AUTHORIZED - EXT2': { label: 'Authorized - Ext2', isCommon: true, priority: 5 },
  'AUTHORIZED - EXTENSION1 PENDING': { label: 'Authorized - Extension1 Pending', isCommon: true, priority: 5 },
  'AUTHORIZED - EXTENSION2 PENDING': { label: 'Authorized - Extension2 Pending', isCommon: true, priority: 5 },
  EXPIRED: { label: 'Expired', isCommon: true, priority: 5 },
  CLOSED: { label: 'Closed', isCommon: true, priority: 5 },
  'CLOSED - ARCHIVED': { label: 'Closed - Archived', isCommon: true, priority: 5 },
};

export const ECOW_ISOLATION_STATUS_MAPPING = {
  IsolationNumberRequired: { label: 'Isolation Number Required', isCommon: true, priority: 0 },
  Draft: { label: 'Draft', isCommon: true, priority: 1 },
  DesignComplete: { label: 'Design Complete', isCommon: true, priority: 2 },
  WaitingForAuthorization: { label: 'Waiting For Authorization', isCommon: true, priority: 3 },

  IsolationInProgress: { label: 'Isolation In Progress', isCommon: false, priority: 4 },
  IsolationInPlace: { label: 'Isolation In Place(LIVE)', isCommon: false, priority: 4 },
  RequestedForSanctionToTest: { label: 'Requested for STT', isCommon: false, priority: 4 },
  IsolationInPlaceForSanctionToTest: {
    label: 'Isolation In Place(STT)',
    isCommon: false,
    priority: 4,
  },
  IsolationInPlaceForLongTerm: {
    label: 'Long Term Isolation In Place',
    isCommon: false,
    priority: 4,
  },
  DeisolationInProgress: { label: 'De-isolation In Progress', isCommon: false, priority: 4 },
  Deisolated: { label: 'De-isolated', isCommon: false, priority: 4 },
  PBUModification: { label: 'PBU Modification', isCommon: false, priority: 4 },
  Closed: { label: 'Closed', isCommon: false, priority: 4 },
  Modification: { label: 'Amendments', isCommon: false, priority: 4 },
  RequestForReisolation: { label: 'Authorised For Re-isolation', isCommon: false, priority: 4 },
};

export const TASK_WORK_CENTER_OPTIONS = [
  'ANLY',
  'AUTO',
  'BCO',
  'BCO_R',
  'CNST',
  'CRAN',
  'ELEC',
  'ELEC_CM',
  'ELEC_D',
  'ELEC_S',
  'FSCM',
  'F_G',
  'HSE',
  'HSE_D',
  'HSE_R',
  'HVAC',
  'ICSS',
  'ICSS_S',
  'INSP',
  'INSPC',
  'INST',
  'INST_D',
  'INSU',
  'LIFT',
  'MAR',
  'MEAS',
  'MECH',
  'MECH_D',
  'MECH_S',
  'OPER',
  'OPER_D',
  'OPER_R',
  'PAIN',
  'PDM',
  'PMF_T',
  'PSV',
  'RAT',
  'RIGG',
  'RSE_D',
  'SAF_D',
  'SCAF',
  'SUBS',
  'SUBS_C',
  'TELE',
  'WELD_D',
  'WIT',
].map(stringToOption);

export const EVENT_TYPE_ENUM: Record<EventTypeEnum, string> = {
  TAR: 'TAR',
  OUTAGE: 'Outage',
};

export const CERTIFICATE_STATUS_ENUM: Record<CertificateStatusEnum, string> = {
  DRAFT: 'Draft',
  ISSUED_FOR_REVIEW: 'Issued for Review',
  APPROVED: 'Approved',
  COMPLETE: 'Complete',
};

export const TEMPLATE_OPTIONS = ['Yes', 'No'].map(stringToOption);
